import { cn } from '@/lib/utils/cn'

/**
 * Universal toolbar pattern (spec §Toolbar).
 *
 *   <Toolbar>
 *     <ToolbarLeft>
 *       <SearchInput />
 *       <ToolbarDivider />
 *       <ViewToggle … />
 *     </ToolbarLeft>
 *     <ToolbarRight>
 *       <ToolbarCount>127 of 340</ToolbarCount>
 *       <Button variant="secondary" size="sm">Export</Button>
 *     </ToolbarRight>
 *   </Toolbar>
 *
 * The filter-chip row is an optional second row rendered below the toolbar
 * when filters are active (Principle 2.2 — one authoritative source for
 * active filter state). Compose with a wrapper when you need it.
 */
export function Toolbar({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      role="toolbar"
      className={cn(
        'flex h-[var(--toolbar-height)] items-center gap-2 border-b border-border-subtle bg-surface-sunk px-3 text-sm',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function ToolbarLeft({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-1 items-center gap-2 min-w-0">{children}</div>
}

export function ToolbarRight({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-2">{children}</div>
}

export function ToolbarDivider() {
  return (
    <span
      aria-hidden="true"
      className="mx-1 inline-block h-4 w-px bg-border-subtle"
    />
  )
}

export function ToolbarCount({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-sans text-xs text-tertiary tabular-nums">{children}</span>
  )
}

/** Row of dismissible filter chips — renders below the toolbar. */
export function FilterChipRow({
  children,
  onClearAll,
  className,
}: {
  children: React.ReactNode
  onClearAll?: () => void
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 border-b border-border-subtle bg-surface px-3 py-2',
        className,
      )}
    >
      {children}
      {onClearAll ? (
        <button
          type="button"
          onClick={onClearAll}
          className="ml-auto cursor-pointer border-none bg-transparent text-xs font-medium text-link hover:text-link-hover"
        >
          Clear all
        </button>
      ) : null}
    </div>
  )
}
